import withStyles from '@mui/styles/withStyles';
import axios from 'axios';
import cx from 'classnames';
import Header from 'components/Header/Header';
import Sidebar from 'components/Sidebar/Sidebar';
import Helmet from 'components/Wrapper/Helmet';
import config from 'config';
import { Context } from 'context/Context';
import mainStyle from 'layouts/style/mainStyle';

import React, { useState, useEffect } from 'react';

import { isImpersonated } from 'helpers/impersonate';

import getRoutes from 'routes/components';
import { routes as administratorRoutes } from 'routes/menu/administrator';
import { routes as associationRoutes } from 'routes/menu/association';
import { routes as collegeRoutes } from 'routes/menu/college';
import { routes as guestRoutes } from 'routes/menu/guest';
import { routes as hrRoutes } from 'routes/menu/hr';
import { routes as inactiveStudentRoutes } from 'routes/menu/inactiveStudent';
import { routes as proRoutes } from 'routes/menu/pro';
import { routes as studentRoutes } from 'routes/menu/student';
import { initializeFirebaseApp } from './../firebase';
import ProfileCollegeModal from 'components/ProfileCollegeModal/ProfileCollegeModal';

import { InstallPWA } from 'components/InstallPWA/InstallPWA';
import { useNavigate } from 'react-router-dom';

const MainLayout = ({ classes, context, ...rest }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [miniActive, setMiniActive] = useState(false);
  const [routes, setRoutes] = useState(null);

  const navigate = useNavigate();

  const backgroundNumber = Math.floor(Math.random() * 3 + 1);
  const newHeader = context.state.user && ['student', 'pro', 'hr'].includes(context.state.user.account.type);

  useEffect(() => {
    getAccount(context).then((account) => {
      determineRoutes(account);
      initFirebase(account);
    });
  }, []);

  useEffect(() => {
    if (context?.state?.user?.account?.type) {
      getAccount(context).then((account) => {
        determineRoutes(account);
      });
    }
  }, [context?.state?.user?.account?.type]);

  const initFirebase = async (account) => {
    if (account && account.type === 'pro') {
      try {
        const firebaseToken = await initializeFirebaseApp();
        // Update user si le token a changé ou si il a été changé
        if (!account.token_web_device || account.token_web_device != firebaseToken) {
          await updateProWithToken(firebaseToken);
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  const updateProWithToken = async (firebaseToken) => {
    axios
      .put(`${config.API_URL}/api/users/${context.state.user._id}`, {
        'account.token_web_device': firebaseToken,
      })
      .catch((e) => {
        console.error(e);
        const message = {
          type: 'danger',
          content: `Erreur lors de la mise à jour du jeton de firebase pour ${context.state.user.email}, error => ${e}`,
        };
        axios.post(`${config.API_URL}/api/logs`, message);
      });
  };

  const handleDrawerToggle = () => setMobileOpen((prev) => !prev);

  const handleDrawerClose = () => setMobileOpen(false);

  const sidebarMinimize = () => setMiniActive((prev) => !prev);

  const hasUserAccountType = (user) => user?.account?.type;

  const isUserProFromBNP = (user) => user.account.type === 'pro' && user.email.split('@')[1] === 'bnpparibas.com';

  const getAccount = async (context) => {
    const { user } = context.state;

    if (hasUserAccountType(user)) {
      if (isImpersonated() || isUserProFromBNP(user)) {
        user.account.is_active = true;
      }
      return context.state.user.account;
    } else {
      return await axios
        .get(`${config.API_URL}/api/users/me`) // TODO: why this returns company: id and not company: Object ?
        .then((res) => {
          context.updateUser({ ...res.data.user });
          return res.data.user.account;
        })
        .catch(() => {
          return null;
        });
    }
  };

  const updateLastConnection = () => {
    const user = context?.state?.user;
    if (!user || !user.account) {
      return;
    }
    const {
      _id,
      account: { type },
    } = user;

    const typeToSaveLastconnection = ['pro', 'rh', 'student', 'college'];
    if (!typeToSaveLastconnection.includes(type)) return;

    if (isImpersonated()) return;

    const body = { 'account.last_connection': Date.now() };
    axios.put(`${config.API_URL}/api/users/${_id}`, body);
  };

  const determineRoutes = (account) => {
    if (!account) {
      if (window.location.search && window.location.search === '?source=1eleve1stage') {
        navigate('/auth/eleve', {
          state: { redirectUri: window.location.href, source: '1eleve1stage' },
        });
      } else {
        return navigate('/auth/inscription');
      }
    }

    if (account.type === 'student' && !account.is_active) {
      setRoutes(inactiveStudentRoutes);
      return;
    }

    switch (account.type) {
      case 'student':
        setRoutes(studentRoutes);
        updateLastConnection();
        break;
      case 'college':
        setRoutes(collegeRoutes);
        updateLastConnection();
        break;
      case 'association':
        setRoutes(associationRoutes);
        break;
      case 'pro':
        setRoutes(proRoutes);
        updateLastConnection();
        break;
      case 'hr':
        setRoutes(hrRoutes);
        updateLastConnection();
        break;
      case 'guest':
        setRoutes(guestRoutes);
        break;
      case 'administrator':
        setRoutes(administratorRoutes);
        break;
      default:
        navigate('/auth/inscription');
        break;
    }
  };

  return (
    routes && (
      <div className={classes.wrapper}>
        {context.state.user.account.type === 'college' && !isImpersonated() && (
          <ProfileCollegeModal user={context.state.user} />
        )}
        <InstallPWA />
        <Helmet />
        <Sidebar
          context={context}
          routes={routes}
          handleDrawerToggle={handleDrawerToggle}
          handleDrawerClose={handleDrawerClose}
          open={mobileOpen}
          color='white'
          bgColor='white'
          miniActive={miniActive}
          newHeader={newHeader}
          {...rest}
        />

        <div
          className={cx({
            [classes.mainPanel]: true,
            [classes[`background${backgroundNumber}`]]: true,
          })}>
          <Header
            className={classes.mobileOnly}
            sidebarMinimize={sidebarMinimize}
            miniActive={miniActive}
            handleDrawerToggle={handleDrawerToggle}
            newHeader={newHeader}
            {...rest}
          />
          <div className={classes.content}>{getRoutes(context.state.user.account.type)}</div>
        </div>
      </div>
    )
  );
};

export default withStyles(mainStyle)((props) => (
  <Context.Consumer>{(context) => <MainLayout {...props} context={context} />}</Context.Consumer>
));
